import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Calendar,
  Clock,
  Users,
  Plus,
  X,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  DollarSign,
  Copy,
  Edit,
  Trash2,
  Filter,
  Download,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { getErrorMessage } from '../utils/errorHandler';

const PlanningAdvanced = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [view, setView] = useState('week'); // week, month
  const [currentDate, setCurrentDate] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sites, setSites] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showConflictsModal, setShowConflictsModal] = useState(false);
  
  const [newShift, setNewShift] = useState({
    agent_id: '',
    site_id: '',
    shift_date: '',
    start_time: '08:00',
    end_time: '16:00',
    shift_type: 'regular'
  });
  
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    start_time: '08:00',
    end_time: '16:00',
    color: '#3B82F6',
    paid_break_minutes: 30,
    unpaid_break_minutes: 0
  });

  const API_URL = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL) || '';

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  useEffect(() => {
    loadData();
  }, [currentDate, view]);

  const loadData = async () => {
    try {
      // Calculer dates de début et fin selon la vue
      const startDate = getStartDate();
      const endDate = getEndDate();
      
      // Charger plages
      const shiftsResponse = await axios.get(
        `${API_URL}/api/planning-advanced/shifts?start_date=${startDate}&end_date=${endDate}`,
        getAuthHeader()
      );
      setShifts(shiftsResponse.data.shifts || []);
      
      // Charger agents
      const agentsResponse = await axios.get(`${API_URL}/api/agents`, getAuthHeader());
      setAgents(agentsResponse.data || []);
      
      // Charger sites
      const sitesResponse = await axios.get(`${API_URL}/api/sites`, getAuthHeader());
      setSites(sitesResponse.data || []);
      
      // Charger gabarits
      const templatesResponse = await axios.get(
        `${API_URL}/api/planning-advanced/templates/shifts`,
        getAuthHeader()
      );
      setShiftTemplates(templatesResponse.data.templates || []);
      
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  };

  const getStartDate = () => {
    const date = new Date(currentDate);
    if (view === 'week') {
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1);
      date.setDate(diff);
    } else {
      date.setDate(1);
    }
    return date.toISOString().split('T')[0];
  };

  const getEndDate = () => {
    const date = new Date(currentDate);
    if (view === 'week') {
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1) + 6;
      date.setDate(diff);
    } else {
      date.setMonth(date.getMonth() + 1, 0);
    }
    return date.toISOString().split('T')[0];
  };

  const createShift = async () => {
    if (!newShift.agent_id || !newShift.site_id || !newShift.shift_date) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs requis",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Trouver agent et site
      const agent = agents.find(a => a.id === newShift.agent_id);
      const site = sites.find(s => s.id === newShift.site_id);
      
      // Calculer durée
      const start = new Date(`2000-01-01T${newShift.start_time}`);
      const end = new Date(`2000-01-01T${newShift.end_time}`);
      const duration = (end - start) / (1000 * 60 * 60);
      
      const shiftData = {
        ...newShift,
        agent_name: agent?.name || '',
        site_name: site?.name || '',
        duration_hours: duration,
        actual_work_hours: duration - (30 / 60), // Moins pause
        status: 'scheduled'
      };

      await axios.post(
        `${API_URL}/api/planning-advanced/shifts`,
        shiftData,
        getAuthHeader()
      );

      toast({
        title: "Plage créée",
        description: "La plage de travail a été créée avec succès",
      });

      setShowShiftModal(false);
      setNewShift({
        agent_id: '',
        site_id: '',
        shift_date: '',
        start_time: '08:00',
        end_time: '16:00',
        shift_type: 'regular'
      });
      loadData();
      
    } catch (error) {
      toast({
        title: "Erreur",
        description: getErrorMessage(error, "Impossible de créer la plage"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async () => {
    if (!newTemplate.name) {
      toast({
        title: "Erreur",
        description: "Veuillez entrer un nom pour le gabarit",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const start = new Date(`2000-01-01T${newTemplate.start_time}`);
      const end = new Date(`2000-01-01T${newTemplate.end_time}`);
      const duration = (end - start) / (1000 * 60 * 60);
      
      const templateData = {
        ...newTemplate,
        duration_hours: duration,
        shift_type: 'regular'
      };

      await axios.post(
        `${API_URL}/api/planning-advanced/templates/shifts`,
        templateData,
        getAuthHeader()
      );

      toast({
        title: "Gabarit créé",
        description: "Le gabarit a été créé avec succès",
      });

      setShowTemplateModal(false);
      setNewTemplate({
        name: '',
        start_time: '08:00',
        end_time: '16:00',
        color: '#3B82F6',
        paid_break_minutes: 30,
        unpaid_break_minutes: 0
      });
      loadData();
      
    } catch (error) {
      toast({
        title: "Erreur",
        description: getErrorMessage(error, "Impossible de créer le gabarit"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const detectConflicts = async () => {
    setLoading(true);
    try {
      const startDate = getStartDate();
      const endDate = getEndDate();
      
      const response = await axios.post(
        `${API_URL}/api/planning-advanced/conflicts/detect`,
        null,
        {
          ...getAuthHeader(),
          params: { start_date: startDate, end_date: endDate }
        }
      );

      setConflicts(response.data.conflicts || []);
      setShowConflictsModal(true);
      
      toast({
        title: "Analyse terminée",
        description: `${response.data.total_conflicts} conflit(s) détecté(s)`,
        variant: response.data.total_conflicts > 0 ? "destructive" : "default",
      });
      
    } catch (error) {
      toast({
        title: "Erreur",
        description: getErrorMessage(error, "Impossible de détecter les conflits"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteShift = async (shiftId) => {
    if (!window.confirm('Supprimer cette plage ?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/planning-advanced/shifts/${shiftId}`,
        getAuthHeader()
      );
      
      toast({
        title: "Plage supprimée",
        description: "La plage a été supprimée",
      });
      
      loadData();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la plage",
        variant: "destructive",
      });
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getWeekDays = () => {
    const startDate = new Date(getStartDate());
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const getShiftsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return shifts.filter(s => s.shift_date === dateStr);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'error': return 'text-orange-600 bg-orange-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-blue-600 bg-blue-50';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Planning Avancé</h1>
          <p className="text-gray-500 mt-1">Gestion professionnelle des plannings agents</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button onClick={() => setShowTemplateModal(true)} variant="outline">
            <Copy className="w-4 h-4 mr-2" />
            Gabarits
          </Button>
          <Button onClick={detectConflicts} variant="outline">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Détecter Conflits
          </Button>
          <Button onClick={() => setShowShiftModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Plage
          </Button>
        </div>
      </div>

      {/* Navigation et Vues */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => view === 'week' ? navigateWeek(-1) : navigateMonth(-1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="text-lg font-semibold">
                {view === 'week' 
                  ? `Semaine du ${getStartDate()}`
                  : currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
                }
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => view === 'week' ? navigateWeek(1) : navigateMonth(1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentDate(new Date())}
              >
                Aujourd'hui
              </Button>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant={view === 'week' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('week')}
              >
                Semaine
              </Button>
              <Button
                variant={view === 'month' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('month')}
              >
                Mois
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vue Semaine */}
      {view === 'week' && (
        <Card>
          <CardContent className="p-0">
            <div className="grid grid-cols-7 divide-x">
              {getWeekDays().map((day, index) => (
                <div key={index} className="min-h-[400px]">
                  <div className="p-3 border-b bg-gray-50">
                    <div className="font-semibold text-sm">
                      {day.toLocaleDateString('fr-FR', { weekday: 'short' })}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {day.getDate()}
                    </div>
                  </div>
                  
                  <div className="p-2 space-y-1">
                    {getShiftsForDate(day).map(shift => (
                      <div
                        key={shift.id}
                        className="p-2 rounded text-xs bg-blue-100 border-l-4 border-blue-600 cursor-pointer hover:bg-blue-200 group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-semibold text-blue-900">
                            {shift.agent_name}
                          </div>
                          <button
                            onClick={() => deleteShift(shift.id)}
                            className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="text-blue-700">
                          {shift.start_time} - {shift.end_time}
                        </div>
                        <div className="text-blue-600">
                          {shift.site_name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal Nouvelle Plage */}
      {showShiftModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Nouvelle Plage de Travail</CardTitle>
                <button onClick={() => setShowShiftModal(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Agent *</Label>
                  <select
                    value={newShift.agent_id}
                    onChange={(e) => setNewShift({...newShift, agent_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Sélectionner un agent</option>
                    {agents.map(agent => (
                      <option key={agent.id} value={agent.id}>{agent.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Site *</Label>
                  <select
                    value={newShift.site_id}
                    onChange={(e) => setNewShift({...newShift, site_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Sélectionner un site</option>
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={newShift.shift_date}
                    onChange={(e) => setNewShift({...newShift, shift_date: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Type</Label>
                  <select
                    value={newShift.shift_type}
                    onChange={(e) => setNewShift({...newShift, shift_type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="regular">Régulière</option>
                    <option value="overtime">Heure sup</option>
                    <option value="training">Formation</option>
                    <option value="on_call">Astreinte</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Heure début *</Label>
                  <Input
                    type="time"
                    value={newShift.start_time}
                    onChange={(e) => setNewShift({...newShift, start_time: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Heure fin *</Label>
                  <Input
                    type="time"
                    value={newShift.end_time}
                    onChange={(e) => setNewShift({...newShift, end_time: e.target.value})}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowShiftModal(false)}>
                  Annuler
                </Button>
                <Button onClick={createShift} disabled={loading}>
                  {loading ? 'Création...' : 'Créer la Plage'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Gabarit */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Gabarits de Plages</CardTitle>
                <button onClick={() => setShowTemplateModal(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Liste gabarits existants */}
              {shiftTemplates.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Gabarits existants</h3>
                  <div className="space-y-2">
                    {shiftTemplates.map(template => (
                      <div key={template.id} className="p-3 border rounded-lg flex items-center justify-between">
                        <div>
                          <div className="font-semibold">{template.name}</div>
                          <div className="text-sm text-gray-600">
                            {template.start_time} - {template.end_time} ({template.duration_hours}h)
                          </div>
                        </div>
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: template.color }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Créer nouveau gabarit */}
              <div className="border-t pt-6">
                <h3 className="font-semibold mb-3">Nouveau gabarit</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nom *</Label>
                    <Input
                      value={newTemplate.name}
                      onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                      placeholder="Ex: Garde de nuit"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Heure début</Label>
                      <Input
                        type="time"
                        value={newTemplate.start_time}
                        onChange={(e) => setNewTemplate({...newTemplate, start_time: e.target.value})}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Heure fin</Label>
                      <Input
                        type="time"
                        value={newTemplate.end_time}
                        onChange={(e) => setNewTemplate({...newTemplate, end_time: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Pause payée (min)</Label>
                      <Input
                        type="number"
                        value={newTemplate.paid_break_minutes}
                        onChange={(e) => setNewTemplate({...newTemplate, paid_break_minutes: parseInt(e.target.value)})}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Pause non payée (min)</Label>
                      <Input
                        type="number"
                        value={newTemplate.unpaid_break_minutes}
                        onChange={(e) => setNewTemplate({...newTemplate, unpaid_break_minutes: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Couleur</Label>
                    <Input
                      type="color"
                      value={newTemplate.color}
                      onChange={(e) => setNewTemplate({...newTemplate, color: e.target.value})}
                    />
                  </div>

                  <Button onClick={createTemplate} disabled={loading} className="w-full">
                    {loading ? 'Création...' : 'Créer le Gabarit'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Conflits */}
      {showConflictsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Conflits Détectés</CardTitle>
                <button onClick={() => setShowConflictsModal(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <CardDescription>
                {conflicts.length} conflit(s) trouvé(s) dans le planning
              </CardDescription>
            </CardHeader>
            <CardContent>
              {conflicts.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-green-900">Aucun conflit détecté !</p>
                  <p className="text-gray-600">Le planning est optimal</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {conflicts.map((conflict, index) => (
                    <div key={index} className={`p-4 rounded-lg border-l-4 ${getSeverityColor(conflict.severity)}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="font-semibold">{conflict.conflict_type}</span>
                            <span className={`text-xs px-2 py-1 rounded ${getSeverityColor(conflict.severity)}`}>
                              {conflict.severity}
                            </span>
                          </div>
                          <p className="text-sm mb-2">{conflict.description}</p>
                          {conflict.suggestion && (
                            <p className="text-xs italic">💡 {conflict.suggestion}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PlanningAdvanced;
